import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { fetchUsers } from '@/service/users'
import type { Assignee, Team } from '@/views/companies/data/schema'
import { getAllTeams, getTeamMembers } from '../service/teams'

export const useTeamStore = defineStore('team', () => {
  const teams = ref<Team[]>([])
  const assigneeUsers = ref<Assignee[]>([
    {
      id: 'unassigned',
      name: 'Unassigned',
      avatar: null,
      email: null,
      role: null,
    },
  ])
  const allAssigneeUsers = ref<Assignee[]>([
    {
      id: 'unassigned',
      name: 'Unassigned',
      avatar: null,
      email: null,
      role: null,
      accessorKey: 'assignee',
    },
  ])
  const users = ref<OrganizationUser[]>([])
  const isLoadingTeams = ref(false)
  const isLoadingAssignees = ref(false)

  const fetchAllMembers = async () => {
    isLoadingAssignees.value = true
    const promiseList = []

    for (const team of teams.value) {
      promiseList.push(
        () =>
          new Promise<void>((resolve) => {
            const { onSuccess, onError } = getTeamMembers(team.id)

            onSuccess((data) => {
              resolve(data)
            })

            onError(() => {
              isLoadingAssignees.value = false
              resolve()
            })
          }),
      )
    }
    const results = await Promise.allSettled(promiseList.map((fn) => fn()))

    allAssigneeUsers.value = results
      .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
      .flatMap((r) => (Array.isArray(r.value) ? r.value : [r.value]))
      .map((user: Assignee) => {
        return {
          ...user,
          accessorKey: 'assignee',
        }
      })
      .filter((user, index, self) => index === self.findIndex((u) => u.id === user.id))
    allAssigneeUsers.value = [
      {
        id: 'unassigned',
        name: 'Unassigned',
        avatar: null,
        email: null,
        role: null,
        accessorKey: 'assignee',
      },
      ...allAssigneeUsers.value,
    ]

    isLoadingAssignees.value = false
  }

  const fetchAllUsers = async (): Promise<OrganizationUser[]> => {
    const { onSuccess, onError } = await fetchUsers()

    return new Promise<OrganizationUser[]>((resolve, reject) => {
      onSuccess((data) => {
        users.value = data
        resolve(data)
      })
      onError((error) => {
        reject(error)
      })
    })
  }
  const fetchTeams = async () => {
    isLoadingTeams.value = true
    isLoadingAssignees.value = true
    const { onSuccess, onError } = getAllTeams()

    return new Promise<void>((resolve) => {
      onSuccess(async (data) => {
        teams.value = data.map((team: Team) => {
          return {
            ...team,
            accessorKey: 'team',
          }
        })
        isLoadingTeams.value = false
        await fetchAllMembers()

        resolve()

        // TEMPORARILY DISABLED: fetchAllUsers() times out with many teams
        fetchAllUsers()
          .then(() => {
            teams.value = teams.value.map((team) => ({
              ...team,
              members: users.value.filter((u) => u?.team?.id === team.id),
            }))
          })
          .catch((err) => {
            console.error('Cannot fetch', err)
          })
      })
      onError(() => {
        isLoadingTeams.value = false
        resolve()
      })
    })
  }

  const fetchTeamMembers = async (id: string) => {
    isLoadingAssignees.value = true
    const { onSuccess, onError } = getTeamMembers(id)

    return new Promise<Assignee[]>((resolve, reject) => {
      onSuccess((data) => {
        assigneeUsers.value = [
          {
            id: 'unassigned',
            name: 'Unassigned',
            avatar: null,
            email: null,
            role: null,
            accessorKey: 'assignee',
          },
          ...data.map((user: Assignee) => {
            return {
              ...user,
              accessorKey: 'assignee',
            }
          }),
        ]
        isLoadingAssignees.value = false
        resolve(data)
      })
      onError((error) => {
        isLoadingAssignees.value = false
        reject(error)
      })
    })
  }

  const setTeams = async (newTeams: Team[]) => {
    teams.value = newTeams
  }

  const getTeams = computed(() => teams.value)

  const getTeamById = (id: string) => {
    return teams.value.find((org) => org.id === id)
  }

  const getUserById = (id: string) => {
    return assigneeUsers.value.find((user) => user.id === id)
  }

  const clearTeams = () => {
    teams.value = []
  }

  const applyTeamChange = (updatedTeam: Team) => {
    const idx = teams.value.findIndex((t) => t.id === updatedTeam.id)

    if (idx !== -1) {
      teams.value[idx] = updatedTeam
    } else {
      teams.value.push(updatedTeam)
    }

    teams.value
      .filter((t) => t.id !== updatedTeam.id)
      .forEach((t) => {
        t.members = t.members.filter((m) => !updatedTeam.members.some((mem) => mem.id === m.id))
      })

    updatedTeam.members.forEach((mem) => {
      const u = users.value.find((u) => u.id === mem.id)

      if (u) u.team = updatedTeam
    })
  }

  function applyUserChange(updatedUser: OrganizationUser) {
    const idx = users.value.findIndex((u) => u.id === updatedUser.id)

    if (idx !== -1) {
      users.value[idx] = updatedUser
    } else {
      users.value.unshift(updatedUser)
    }

    teams.value.forEach((team) => {
      if (team.id === updatedUser.team?.id) {
        if (!team.members.some((m) => m.id === updatedUser.id)) {
          team.members.push(updatedUser)
        } else {
          team.members = team.members.map((m) => (m.id === updatedUser.id ? updatedUser : m))
        }
      } else {
        team.members = team.members.filter((m) => m.id !== updatedUser.id)
      }
    })
  }

  const updateQueueAffiliationInTeams = (
    queueId: string,
    queueName: string,
    queueSlug: string,
    prevTeamIds: string[],
    newTeamIds: string[],
  ) => {
    prevTeamIds.forEach((tid) => {
      if (!newTeamIds.includes(tid)) {
        const team = teams.value.find((t) => t.id === tid)

        if (team) {
          if (!Array.isArray(team.queues)) team.queues = []
          team.queues = (team.queues as { id: string; name: string; slug: string }[]).filter((q) => q.id !== queueId)
        }
      }
    })
    newTeamIds.forEach((tid) => {
      const team = teams.value.find((t) => t.id === tid)

      if (team) {
        if (!Array.isArray(team.queues)) team.queues = []
        if (!(team.queues as { id: string; name: string; slug: string }[]).some((q) => q.id === queueId)) {
          ;(team.queues as { id: string; name: string; slug: string }[]).push({
            id: queueId,
            name: queueName,
            slug: queueSlug,
          })
        }
      }
    })
  }

  return {
    teams,
    fetchTeams,
    setTeams,
    getTeamById,
    getUserById,
    clearTeams,
    getTeams,
    isLoadingTeams,
    isLoadingAssignees,
    assigneeUsers,
    allAssigneeUsers,
    fetchTeamMembers,
    fetchAllMembers,
    fetchAllUsers,
    applyTeamChange,
    applyUserChange,
    users,
    updateQueueAffiliationInTeams,
  }
})
